import type { Instance } from 'mobx-state-tree';
import { types } from 'mobx-state-tree';
import { createContext, useContext } from 'react';

import { ApiStatus } from '../utilities/enums';
import { AppVersion } from './AppVersion';
import { CompletionDate } from './CompletionDate';
import { CurrentUserSelected } from './CurrentUserSelected';
import { DeficiencyFileCount } from './deficiencyFilesCount';
import { configCatFlag } from './FeatureFlag';
import { FilesCount } from './filesCount';
import { HandleGesture } from './HandleGesture';
import { InspectionFileCount } from './inspectionFileCount';
import PrimaryContext from './PrimaryContext';
import { SaveChecklist } from './SaveChecklist';
import { saveInspection } from './SaveInspection';
import { SavePMChecklist } from './SavePMChecklist';
import { SiteDiscipline } from './SiteDiscipline';
import { SyncData } from './syncData';
import { uploadFileProgress } from './uploadFileProgress';
import { WorkPackageTabs } from './WorkPackageTabs';

const currentAppVersion = import.meta.env.VITE_APP_VERSION || '';

export const RootModel = types.model({
  appVersion: AppVersion,
  primaryContext: PrimaryContext,
  SaveChecklist,
  SavePMChecklist,
  inspectionFilesCount: InspectionFileCount,
  syncData: SyncData,
  saveInspection,
  UploadFileProgress: uploadFileProgress,
  CompletionDate,
  SiteDiscipline,
  deficiencyFileCount: DeficiencyFileCount,
  filesCount: FilesCount,
  HandleGesture,
  configCatFlag,
  WorkPackageTabs,
  CurrentUserSelected,
});

const selectedSite = JSON.parse(localStorage.getItem('selectedSite') || `{}`);
const selectedDiscipline = JSON.parse(localStorage.getItem('selectedDiscipline') || `{}`);

export const rootStore = RootModel.create({
  appVersion: {
    currentAppVersion,
    hasNewUpdate: false,
  },
  primaryContext: {
    PrimaryContextListFetchStatus: ApiStatus.NOT_CALLED,
    PrimaryContextList: [],
    WorkPackageList: [],
    WorkPackageListFetchStatus: ApiStatus.NOT_CALLED,
    InspectionList: [],
    InspectionListFetchStatus: ApiStatus.NOT_CALLED,
    wpFiles: [],
    wpFilesFetchStatus: ApiStatus.NOT_CALLED,
  },
  SaveChecklist: {
    isChanged: false,
    tempData: [{}],
  },
  SavePMChecklist: {
    isChanged: false,
    tempData: [{}],
  },
  inspectionFilesCount: {
    inspectionFileCount: 0,
  },
  syncData: {
    syncDataStatus: false,
    workpackageSync: false,
    masterDataSync: false,
    inspectionFilesSync: false,
    deficiencyFilesSync: false,
    previousReportSync: false,
    workpackageFilesSync: false,
    componentTypeListSync: false,
    remedialActionListSync: false,
    primaryCauseOfFailureListSync: false,
    conditionRatingListSync: false,
    urgencyListSync: false,
    operationalDowntimeListSync: false,
    trafficSafetyListSync: false,
    totalDownloadCount: 0,
    fixTotalCount: 0,
    isSyncing: false,
  },
  saveInspection: {
    isChanged: false,
    tempData: {},
    isSaved: true,
  },
  UploadFileProgress: {
    fileUUID: '',
    uploadValue: 0,
  },
  CompletionDate: {
    completionDate: [],
  },
  SiteDiscipline: {
    site: selectedSite ? selectedSite?.selectedSite : '',
    discipline: selectedDiscipline ? selectedDiscipline?.selectedDiscipline : '',
    wpCount: Number(localStorage.getItem('wpCount')),
    refreshWorkPackage: false,
    siteId: selectedSite?.selectedSiteId || 0,
    disciplineId: selectedDiscipline?.selectedDisciplineId || 0,
    siteDisciplineUpdateInitialSync: false,
  },
  deficiencyFileCount: {
    deficiencyFileCount: 0,
  },
  filesCount: {
    wpfilesCount: 0,
    assetfilescount: 0,
    previousreportfilescount: 0,
    signandstampfilescount: 0,
    signandstampUserLevelfilescount: 0,
  },
  HandleGesture: {
    bulkUpload: false,
    gestureForUnAssigned: false,
    gestureForReAssign: false,
    gestureForSignAndStamp: false,
    isLIstampingRequired: false,
    comingFromWorkPackage: false,
    isNotificationClicked: false,
    isDefaultLiSignAndStamp: false,
    inspectionIds: [],
    taskIds: [],
    selectAll: false,
    inspectionStatusId: 0,
    inspectionTask: [],
    handleAccordion: [],
    isCompleted: false,
    runOnInspectionSub: false,
    runOnTaskSub: false,
    runOnDeletion: false,
    tasksforReAssignAndSign: [],
    isManualSyncClicked: false,
    errorInManualSync: false,
    lastManualSync: 0,
  },
  WorkPackageTabs: {
    tab: localStorage.getItem('isAdhoc')?.toLowerCase() === 'true' ? 1 : 0,
  },
  configCatFlag: {
    blueBanner: {},
    redBanner: {},
  },
  CurrentUserSelected: {
    currentUser: localStorage.getItem('currentUser')
      ? JSON.parse(localStorage.getItem('currentUser') || `{}`)
      : null,
    workPackageId: Number(localStorage.getItem('workPackageId')) || 0,
    inspectionId: Number(localStorage.getItem('inspectionID')) || 0,
    taskId: Number(localStorage.getItem('taskId')) || 0,
    assetId: Number(localStorage.getItem('assetId')) || 0,
    isAdhoc: localStorage.getItem('isAdhoc')?.toLowerCase() === 'true' || false,
  },
});

export type RootInstance = Instance<typeof RootModel>;
const RootStoreContext = createContext<null | RootInstance>(null);

export const { Provider } = RootStoreContext;
export const MSTProvider = RootStoreContext.Provider;

export const useMst = () => {
  const store = useContext(RootStoreContext);
  if (store === null) {
    throw new Error('Store cannot be null, please add a context provider');
  }
  return store;
};
